import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import {
  BINARY_DECOMPOSITION,
  INVENTORY_UI,
  SNIPPET_DETECTION,
  VENDORED_DEPENDENCY_DETECTION,
} from '../../../../../shared/featureFlags/flagNames';
import { useIsFeatureEnabled } from '../../../../helpers/features';
import { useCurrentProject } from '../../../../hooks/api/projects/useCurrentProject';
import releaseGroupSelectors from '../../../../redux/project_grouping/selectors';
import { checkProjectScope } from '../types';
import type { ProjectType } from '../../../../../typings/enums';
import type { ProjectGroupRelease } from '../../../../types';
import type { InventoryScope, UseInventoryFeaturesResult } from '../types';

const shouldShowFeatureForReleaseGroup = (
  release: ProjectGroupRelease | null,
  isFeatureEnabled: boolean,
  supportedProjectTypes: ProjectType[],
): boolean => {
  return isFeatureEnabled && (
    release?.projects?.some(project => supportedProjectTypes.includes(project.project.type)) ?? false
  );
};

const shouldShowFeatureForProject = (
  projectType: ProjectType | null,
  isFeatureEnabled: boolean,
  supportedProjectTypes: ProjectType[],
): boolean => {
  return isFeatureEnabled && supportedProjectTypes.includes(projectType);
};

/**
 * A hook for figuring out which features of the inventory dropdown should be shown.
 *
 * @param {InventoryScope} scope - The scope of the inventory dropdown.
 *
 */
export function useInventoryFeatures(scope: InventoryScope): UseInventoryFeaturesResult {
  const isProjectScope = checkProjectScope(scope);

  //  Note: that this part relies on the Release Groups Redux state
  const release = useSelector(releaseGroupSelectors.getRelease);
  const project = useCurrentProject();
  const projectType = project.data?.type ?? null;

  //  Feature flag checks
  const isInventoryUIEnabled = useIsFeatureEnabled(INVENTORY_UI);
  const isSnippetScanningEnabled = useIsFeatureEnabled(SNIPPET_DETECTION);
  const isBinaryDecompositionEnabled = useIsFeatureEnabled(BINARY_DECOMPOSITION);
  const isVendoredDependenciesEnabled = useIsFeatureEnabled(VENDORED_DEPENDENCY_DETECTION);

  // Only consider True for snippets if we're looking at a project, since RG snippets are not supported yet.
  const shouldShowSnippets = (
    isProjectScope &&
    shouldShowFeatureForProject(projectType, isSnippetScanningEnabled, ['autobuild', 'provided'])
  );
  // Memoize these two results because we don't want to iterate release.projects on every render.
  // Vendored dependencies are available for both project and release group scopes
  const shouldShowVendored = useMemo(() => isProjectScope
    ? shouldShowFeatureForProject(projectType, isVendoredDependenciesEnabled, ['autobuild', 'provided'])
    : shouldShowFeatureForReleaseGroup(release, isVendoredDependenciesEnabled, ['autobuild', 'provided']),
  [isProjectScope, isVendoredDependenciesEnabled, projectType, release]);
  const shouldShowBinaryAnalysis = useMemo(() => isProjectScope
    ? shouldShowFeatureForProject(projectType, isBinaryDecompositionEnabled, ['binary'])
    : shouldShowFeatureForReleaseGroup(release, isBinaryDecompositionEnabled, ['binary']),
  [isProjectScope, isBinaryDecompositionEnabled, projectType, release]);

  const shouldShowAnyGatedFeatures = shouldShowSnippets || shouldShowBinaryAnalysis || shouldShowVendored;
  //  They need to have at least one of the gated features enabled to show the inventory dropdown
  const shouldShowInventory = isInventoryUIEnabled && shouldShowAnyGatedFeatures;

  return useMemo(() => ({
    shouldShowInventory,
    shouldShowSnippets,
    shouldShowBinaryAnalysis,
    shouldShowVendored,
  }), [shouldShowInventory, shouldShowSnippets, shouldShowBinaryAnalysis, shouldShowVendored]);
}
